/**
 * PRP-IMPORT-PRICENOTE-1 item 4: text printed where a price goes that AIEA did not recognize.
 * The lines export as they always did. Adding the text to the list means a re-import reads it.
 */
import { useMemo, useState } from 'react';

import { PriceNotesAPI } from '../../lib/priceNotesApi';
import type { ParsedEstimateResponse } from '../../types';
import { parkedPriceTexts, type ParkedText } from '../lib/priceNoteLines';
import { IconWarn } from './icons';
import { Banner } from './ui/banner';
import { Button } from './ui/button';

function ParkedRow({ parked }: { parked: ParkedText }) {
  const [saving, setSaving] = useState(false);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const count = parked.codes.length;

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await PriceNotesAPI.add(parked.text);
      setAdded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add it. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-[6px]">
      <div className="min-w-0">
        <span className="font-[560]">{parked.text}</span>
        <span className="text-[var(--tx-lo)]">
          {' '}
          on {count} {count === 1 ? 'line' : 'lines'}: {parked.codes.join(', ')}
        </span>
        {error && (
          <div role="alert" className="text-[var(--bad)]">
            {error}
          </div>
        )}
      </div>
      {added ? (
        <span className="text-[var(--tx-lo)]">It is on your list. A re-import reads it.</span>
      ) : (
        <Button size="sm" disabled={saving} onClick={() => void add()}>
          Add to my list
        </Button>
      )}
    </li>
  );
}

export default function ParkedPriceNotice({ result }: { result: ParsedEstimateResponse }) {
  const parked = useMemo(() => parkedPriceTexts(result), [result]);
  if (parked.length === 0) return null;
  return (
    <Banner tone="warn" icon={<IconWarn />} className="mb-3">
      <div data-testid="parked-price-notice">
        <div className="font-[580]">Price text not recognized</div>
        <div className="text-[var(--tx-lo)]">
          These lines show text where the price goes. It is not on your price note list.
        </div>
        <ul className="m-0 mt-1 p-0 list-none">
          {parked.map((p) => (
            <ParkedRow key={p.text} parked={p} />
          ))}
        </ul>
      </div>
    </Banner>
  );
}
